import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type DemographicAnalysis } from '../../demographicsService'
import {
  HouseholdCompositionChart,
  type HouseholdCompositionChartRef,
} from '../charts/HouseholdCompositionChart'
import {
  CommunityStabilityChart,
  type CommunityStabilityChartRef,
} from '../charts/CommunityStabilityChart'
import {
  NationalityChart,
  type NationalityChartRef,
} from '../charts/NationalityChart'
import {
  MigrationChart,
  type MigrationChartRef,
} from '../charts/MigrationChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartGrid,
  ChartContainer,
} from '../../DemographicsTab.styles'

export interface MarketCompositionTabProps {
  data: DemographicAnalysis | null
  t: TFunction
}

export const MarketCompositionTab: React.FC<MarketCompositionTabProps> = ({
  data,
  t,
}) => {
  const householdCompositionChartRef =
    useRef<HouseholdCompositionChartRef>(null)
  const communityStabilityChartRef = useRef<CommunityStabilityChartRef>(null)
  const nationalityChartRef = useRef<NationalityChartRef>(null)
  const migrationChartRef = useRef<MigrationChartRef>(null)

  if (!data) return null

  const { current } = data

  // Calculate key metrics
  const averageHouseholdSize =
    Math.round(
      (current.total_population / current.total_households_number) * 10
    ) / 10
  const singleHouseholdRatio = Math.round(
    (current.total_households_1 / current.total_households_number) * 100
  )
  const twoPersonHouseholdRatio = Math.round(
    (current.total_households_2 / current.total_households_number) * 100
  )
  const familyHouseholdRatio = Math.round(
    ((current.total_households_3 +
      current.total_households_4 +
      current.total_households_5 +
      current.total_households_6_plus) /
      current.total_households_number) *
      100
  )

  // Stability metrics
  const longTermResidents =
    current.number_residents_city_10_plus_year +
    current.number_residents_city_since_birth
  const stabilityPercentage = Math.round(
    (longTermResidents / current.total_population) * 100
  )
  const newResidentsPercentage = Math.round(
    (current.number_residents_city_0_1_year / current.total_population) * 100
  )

  const getStabilityVariant = (percentage: number) => {
    if (percentage >= 70) return 'success'
    if (percentage >= 50) return 'default'
    if (percentage >= 30) return 'warning'
    return 'danger'
  }

  return (
    <TabContentContainer>
      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.householdSize.title')}
          value={t('metrics.averageHouseholdSize.people', {
            count: averageHouseholdSize,
          })}
          subtitle={t('metrics.householdSize.subtitle', {
            count: current.total_households_number.toLocaleString(),
          })}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.averageHouseholdSize.title')}
              shortDescription={t(
                'tooltips.averageHouseholdSize.shortDescription'
              )}
              calculation={t('tooltips.averageHouseholdSize.calculation')}
              importance={t('tooltips.averageHouseholdSize.importance')}
              limitations={t('tooltips.averageHouseholdSize.limitations')}
              source={t('tooltips.averageHouseholdSize.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.singlePersonHouseholds.title')}
          value={`${singleHouseholdRatio}%`}
          subtitle={t('metrics.singlePersonHouseholds.subtitle', {
            count: current.total_households_1.toLocaleString(),
          })}
          variant={singleHouseholdRatio > 40 ? 'primary' : 'default'}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.singlePersonHouseholds.title')}
              shortDescription={t(
                'tooltips.singlePersonHouseholds.shortDescription'
              )}
              calculation={t('tooltips.singlePersonHouseholds.calculation')}
              importance={t('tooltips.singlePersonHouseholds.importance')}
              limitations={t('tooltips.singlePersonHouseholds.limitations')}
              source={t('tooltips.singlePersonHouseholds.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.familyHouseholds.title')}
          value={`${familyHouseholdRatio}%`}
          subtitle={t('metrics.familyHouseholds.subtitle', {
            count: (
              current.total_households_3 +
              current.total_households_4 +
              current.total_households_5 +
              current.total_households_6_plus
            ).toLocaleString(),
          })}
          variant={familyHouseholdRatio > 40 ? 'success' : 'default'}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.familyHouseholds.title')}
              shortDescription={t('tooltips.familyHouseholds.shortDescription')}
              calculation={t('tooltips.familyHouseholds.calculation')}
              importance={t('tooltips.familyHouseholds.importance')}
              limitations={t('tooltips.familyHouseholds.limitations')}
              source={t('tooltips.familyHouseholds.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.communityStability.title')}
          value={`${stabilityPercentage}%`}
          subtitle={t('metrics.communityStability.subtitle', {
            count: longTermResidents.toLocaleString(),
          })}
          variant={getStabilityVariant(stabilityPercentage)}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.communityStability.title')}
              shortDescription={t(
                'tooltips.communityStability.shortDescription'
              )}
              calculation={t('tooltips.communityStability.calculation')}
              importance={t('tooltips.communityStability.importance')}
              limitations={t('tooltips.communityStability.limitations')}
              source={t('tooltips.communityStability.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.newResidents.title')}
          value={`${newResidentsPercentage}%`}
          subtitle={t('metrics.newResidents.subtitle', {
            count: current.number_residents_city_0_1_year.toLocaleString(),
          })}
          variant={newResidentsPercentage > 15 ? 'warning' : 'default'}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.newResidents.title')}
              shortDescription={t('tooltips.newResidents.shortDescription')}
              calculation={t('tooltips.newResidents.calculation')}
              importance={t('tooltips.newResidents.importance')}
              limitations={t('tooltips.newResidents.limitations')}
              source={t('tooltips.newResidents.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.twoPersonHouseholds.title')}
          value={`${twoPersonHouseholdRatio}%`}
          subtitle={t('metrics.twoPersonHouseholds.subtitle', {
            count: current.total_households_2.toLocaleString(),
          })}
          variant={twoPersonHouseholdRatio > 30 ? 'primary' : 'default'}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.twoPersonHouseholds.title')}
              shortDescription={t(
                'tooltips.twoPersonHouseholds.shortDescription'
              )}
              calculation={t('tooltips.twoPersonHouseholds.calculation')}
              importance={t('tooltips.twoPersonHouseholds.importance')}
              limitations={t('tooltips.twoPersonHouseholds.limitations')}
              source={t('tooltips.twoPersonHouseholds.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Charts */}
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.householdComposition.title')}
            subtitle={t('charts.householdComposition.subtitle')}
            onDownload={() => {
              const dataUrl =
                householdCompositionChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.householdComposition.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <HouseholdCompositionChart
            ref={householdCompositionChartRef}
            data={current}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.communityStability.title')}
            subtitle={t('charts.communityStability.subtitle')}
            onDownload={() => {
              const dataUrl = communityStabilityChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.communityStability.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <CommunityStabilityChart
            ref={communityStabilityChartRef}
            data={current}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.nationalityComposition.title')}
            subtitle={t('charts.nationalityComposition.subtitle')}
            onDownload={() => {
              const dataUrl = nationalityChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.nationalityComposition.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <NationalityChart
            ref={nationalityChartRef}
            data={{
              swiss: current.total_population_swiss,
              foreign: current.total_population_foreign,
            }}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.migrationOrigins.title')}
            subtitle={t('charts.migrationOrigins.subtitle')}
            onDownload={() => {
              const dataUrl = migrationChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.migrationOrigins.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <MigrationChart
            ref={migrationChartRef}
            data={{
              local: current.number_residents_1_year_same_city,
              cantonal: current.number_residents_1_year_same_canton,
              interCantonal: current.number_residents_1_year_other_canton,
              international: current.number_residents_1_year_foreign,
              newBorn: current.number_residents_1_year_not_born,
            }}
            t={t}
          />
        </ChartContainer>
      </ChartGrid>
    </TabContentContainer>
  )
}
